<template>
  <div>
    <head>
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
      <div id="layout-wrapper">
        <NavigationBar />

        <div id="content" class="content-minimize">
          <div class="page-content">
            <div class="container-fluid">
              <!-- Page Title -->
              <div class="row">
                <div class="col-12">
                  <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                    <h4 class="mb-sm-0 font-size-18">Patient Medical History</h4>
                    <div class="page-title-right">
                      <ol class="breadcrumb m-0">
                        <li class="breadcrumb-item"><a href="javascript: void(0);">Hospital</a></li>
                        <li class="breadcrumb-item"><a href="javascript: void(0);" @click="goTo('patient-list')">Patients</a></li>
                        <li class="breadcrumb-item active">History</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Patient Summary Card -->
              <div v-if="patient" class="row mb-4">
                <div class="col-12">
                  <div class="card">
                    <div class="card-body py-3">
                      <div class="row align-items-center">
                        <div class="col-md-6">
                          <div class="d-flex align-items-center">
                            <div class="avatar-sm me-3">
                              <span class="avatar-title rounded-circle" :class="patient.gender === 'Male' ? 'bg-primary' : 'bg-pink'">
                                {{ getInitials(patient.fullNames) }}
                              </span>
                            </div>
                            <div>
                              <h5 class="mb-0">{{ patient.fullNames }}</h5>
                              <small class="text-muted">{{ patient.patientID }} | {{ patient.gender }}, {{ patient.age }} yrs</small>
                            </div>
                          </div>
                        </div>
                        <div class="col-md-6 text-md-end mt-3 mt-md-0">
                          <span v-if="patient.allergies" class="badge bg-danger me-2"><i class="mdi mdi-alert me-1"></i>Allergies: {{ patient.allergies }}</span>
                          <span v-if="patient.chronicConditions" class="badge bg-warning text-dark"><i class="mdi mdi-heart-pulse me-1"></i>{{ patient.chronicConditions }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Stats Cards -->
              <div class="row mb-4">
                <div class="col-xl-2 col-md-4 col-6">
                  <div class="card mini-stats-wid">
                    <div class="card-body py-3 text-center">
                      <h4 class="mb-0 text-primary">{{ stats.totalVisits }}</h4>
                      <small class="text-muted">Total Visits</small>
                    </div>
                  </div>
                </div>
                <div class="col-xl-2 col-md-4 col-6">
                  <div class="card mini-stats-wid">
                    <div class="card-body py-3 text-center">
                      <h4 class="mb-0 text-success">{{ stats.admissions }}</h4>
                      <small class="text-muted">Admissions</small>
                    </div>
                  </div>
                </div>
                <div class="col-xl-2 col-md-4 col-6">
                  <div class="card mini-stats-wid">
                    <div class="card-body py-3 text-center">
                      <h4 class="mb-0 text-info">{{ stats.labTests }}</h4>
                      <small class="text-muted">Lab Tests</small>
                    </div>
                  </div>
                </div>
                <div class="col-xl-2 col-md-4 col-6">
                  <div class="card mini-stats-wid">
                    <div class="card-body py-3 text-center">
                      <h4 class="mb-0 text-warning">{{ stats.prescriptions }}</h4>
                      <small class="text-muted">Prescriptions</small>
                    </div>
                  </div>
                </div>
                <div class="col-xl-2 col-md-4 col-6">
                  <div class="card mini-stats-wid">
                    <div class="card-body py-3 text-center">
                      <h4 class="mb-0 text-secondary">{{ stats.procedures }}</h4>
                      <small class="text-muted">Procedures</small>
                    </div>
                  </div>
                </div>
                <div class="col-xl-2 col-md-4 col-6">
                  <div class="card mini-stats-wid">
                    <div class="card-body py-3 text-center">
                      <h4 class="mb-0 text-danger">{{ stats.referrals }}</h4>
                      <small class="text-muted">Referrals</small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Filters Card -->
              <div class="card mb-4">
                <div class="card-body py-2">
                  <div class="row g-2 align-items-center">
                    <div class="col-md-3">
                      <select class="form-select form-select-sm" v-model="filters.type" @change="loadHistory">
                        <option value="">All Record Types</option>
                        <option value="visits">Visits Only</option>
                        <option value="admissions">Admissions</option>
                        <option value="lab">Lab Results</option>
                        <option value="prescriptions">Prescriptions</option>
                        <option value="procedures">Procedures</option>
                        <option value="referrals">Referrals</option>
                      </select>
                    </div>
                    <div class="col-md-2">
                      <input type="date" class="form-control form-control-sm" v-model="filters.dateFrom" @change="loadHistory">
                    </div>
                    <div class="col-md-2">
                      <input type="date" class="form-control form-control-sm" v-model="filters.dateTo" @change="loadHistory">
                    </div>
                    <div class="col-md-3">
                      <select class="form-select form-select-sm" v-model="filters.facility" @change="loadHistory">
                        <option value="">All Facilities</option>
                        <option v-for="f in facilities" :key="f.facilityID" :value="f.facilityID">{{ f.facilityName }}</option>
                      </select>
                    </div>
                    <div class="col-md-2">
                      <div class="btn-group btn-group-sm w-100">
                        <button class="btn" :class="viewMode === 'timeline' ? 'btn-primary' : 'btn-outline-primary'" @click="viewMode = 'timeline'"><i class="mdi mdi-format-list-bulleted"></i></button>
                        <button class="btn" :class="viewMode === 'table' ? 'btn-primary' : 'btn-outline-primary'" @click="viewMode = 'table'"><i class="mdi mdi-table"></i></button>
                        <button class="btn btn-outline-secondary" @click="exportHistory"><i class="mdi mdi-download"></i></button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Loading -->
              <div v-if="loading" class="text-center py-5">
                <div class="spinner-border text-primary mb-3"></div>
                <p class="text-muted">Loading medical history...</p>
              </div>

              <!-- No Records -->
              <div v-else-if="history.length === 0" class="card">
                <div class="card-body text-center py-5">
                  <div class="avatar-lg mx-auto mb-4">
                    <span class="avatar-title bg-light text-muted rounded-circle"><i class="mdi mdi-file-document-outline font-size-48"></i></span>
                  </div>
                  <h5 class="text-muted">No Medical Records Found</h5>
                  <p class="text-muted">No history matching your filters</p>
                </div>
              </div>

              <!-- Timeline View -->
              <div v-else-if="viewMode === 'timeline'" class="timeline">
                <div v-for="(item, index) in history" :key="item.id" class="timeline-item" :class="{ 'timeline-item-right': index % 2 === 1 }">
                  <div class="timeline-marker" :class="getMarkerClass(item.type)">
                    <i :class="getTypeIcon(item.type)"></i>
                  </div>
                  <div class="timeline-content">
                    <div class="card">
                      <div class="card-header py-2 d-flex justify-content-between align-items-center">
                        <div>
                          <span class="badge me-2" :class="getTypeBadgeClass(item.type)">{{ item.type }}</span>
                          <strong>{{ item.title }}</strong>
                        </div>
                        <small class="text-muted">{{ formatDateTime(item.date) }}</small>
                      </div>
                      <div class="card-body py-2">
                        <template v-if="item.type === 'Visit'">
                          <p class="mb-1"><strong>Facility:</strong> {{ item.facilityName }}</p>
                          <p class="mb-1"><strong>Department:</strong> {{ item.departmentName }}</p>
                          <p class="mb-1" v-if="item.chiefComplaint"><strong>Chief Complaint:</strong> {{ item.chiefComplaint }}</p>
                          <p class="mb-0" v-if="item.diagnosis"><strong>Diagnosis:</strong> {{ item.diagnosis }}</p>
                        </template>
                        <template v-else-if="item.type === 'Lab Result'">
                          <p class="mb-1"><strong>Test:</strong> {{ item.testName }}</p>
                          <p class="mb-1"><strong>Result:</strong> <span :class="{ 'text-danger': item.abnormal }">{{ item.result }} {{ item.unit }} <i v-if="item.abnormal" class="mdi mdi-alert"></i></span></p>
                          <p class="mb-0" v-if="item.normalRange"><strong>Normal Range:</strong> {{ item.normalRange }}</p>
                        </template>
                        <template v-else-if="item.type === 'Prescription'">
                          <div v-for="drug in item.drugs" :key="drug.itemID" class="mb-1"><strong>{{ drug.drugName }}</strong> - {{ drug.dosage }} {{ drug.frequency }} <span class="text-muted">({{ drug.duration }})</span></div>
                          <p class="mb-0"><strong>Prescribed by:</strong> {{ item.prescriberName }}</p>
                        </template>
                        <template v-else-if="item.type === 'Admission'">
                          <p class="mb-1"><strong>Ward:</strong> {{ item.wardName }}</p>
                          <p class="mb-1"><strong>Bed:</strong> {{ item.bedNumber }}</p>
                          <p class="mb-1"><strong>Diagnosis:</strong> {{ item.admittingDiagnosis }}</p>
                          <p class="mb-0" v-if="item.dischargeDate"><strong>Discharged:</strong> {{ formatDate(item.dischargeDate) }}</p>
                        </template>
                        <template v-else-if="item.type === 'Procedure'">
                          <p class="mb-1"><strong>Procedure:</strong> {{ item.procedureName }}</p>
                          <p class="mb-1" v-if="item.surgeonName"><strong>Surgeon:</strong> {{ item.surgeonName }}</p>
                          <p class="mb-0" v-if="item.findings"><strong>Findings:</strong> {{ item.findings }}</p>
                        </template>
                        <template v-else-if="item.type === 'Referral'">
                          <p class="mb-1"><strong>From:</strong> {{ item.fromFacilityName }}</p>
                          <p class="mb-1"><strong>To:</strong> {{ item.toFacilityName }}</p>
                          <p class="mb-0"><strong>Reason:</strong> {{ item.referralReason }}</p>
                        </template>
                        <template v-else><p class="mb-0">{{ item.description }}</p></template>
                      </div>
                      <div class="card-footer py-1 bg-light">
                        <small class="text-muted"><i class="mdi mdi-hospital-building me-1"></i>{{ item.facilityName }} <span v-if="item.doctorName" class="ms-3"><i class="mdi mdi-doctor me-1"></i>{{ item.doctorName }}</span></small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Table View -->
              <div v-else class="card">
                <div class="table-responsive">
                  <table class="table table-hover mb-0">
                    <thead class="table-light">
                      <tr><th>Date</th><th>Type</th><th>Description</th><th>Facility</th><th>Doctor</th><th>Action</th></tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in history" :key="item.id">
                        <td><small>{{ formatDateTime(item.date) }}</small></td>
                        <td><span class="badge" :class="getTypeBadgeClass(item.type)">{{ item.type }}</span></td>
                        <td><strong>{{ item.title }}</strong><br><small class="text-muted">{{ truncate(item.description, 50) }}</small></td>
                        <td><small>{{ item.facilityName }}</small></td>
                        <td><small>{{ item.doctorName || '-' }}</small></td>
                        <td><button class="btn btn-sm btn-soft-primary" @click="viewDetails(item)"><i class="mdi mdi-eye"></i></button></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Pagination -->
              <nav v-if="totalPages > 1" class="mt-4">
                <ul class="pagination justify-content-center mb-0">
                  <li class="page-item" :class="{ disabled: currentPage === 1 }"><a class="page-link" href="#" @click.prevent="goToPage(currentPage - 1)"><i class="mdi mdi-chevron-left"></i></a></li>
                  <li v-for="page in visiblePages" :key="page" class="page-item" :class="{ active: page === currentPage }"><a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a></li>
                  <li class="page-item" :class="{ disabled: currentPage === totalPages }"><a class="page-link" href="#" @click.prevent="goToPage(currentPage + 1)"><i class="mdi mdi-chevron-right"></i></a></li>
                </ul>
              </nav>

              <!-- Detail Modal -->
              <div class="modal fade" id="detailModal" tabindex="-1" ref="detailModal">
                <div class="modal-dialog modal-lg">
                  <div class="modal-content">
                    <div class="modal-header">
                      <h5 class="modal-title"><span class="badge me-2" :class="getTypeBadgeClass(selectedItem?.type)">{{ selectedItem?.type }}</span>{{ selectedItem?.title }}</h5>
                      <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body" v-if="selectedItem">
                      <div class="row g-3">
                        <div class="col-md-6"><label class="text-muted small">Date</label><p class="mb-2">{{ formatDateTime(selectedItem.date) }}</p></div>
                        <div class="col-md-6"><label class="text-muted small">Facility</label><p class="mb-2">{{ selectedItem.facilityName }}</p></div>
                        <div class="col-md-6" v-if="selectedItem.departmentName"><label class="text-muted small">Department</label><p class="mb-2">{{ selectedItem.departmentName }}</p></div>
                        <div class="col-md-6" v-if="selectedItem.doctorName"><label class="text-muted small">Doctor</label><p class="mb-2">{{ selectedItem.doctorName }}</p></div>
                        <div class="col-12" v-if="selectedItem.chiefComplaint"><label class="text-muted small">Chief Complaint</label><p class="mb-2">{{ selectedItem.chiefComplaint }}</p></div>
                        <div class="col-12" v-if="selectedItem.diagnosis"><label class="text-muted small">Diagnosis</label><p class="mb-2">{{ selectedItem.diagnosis }}</p></div>
                        <div class="col-12" v-if="selectedItem.treatmentPlan"><label class="text-muted small">Treatment Plan</label><p class="mb-2">{{ selectedItem.treatmentPlan }}</p></div>
                        <div class="col-12" v-if="selectedItem.notes"><label class="text-muted small">Notes</label><p class="mb-0">{{ selectedItem.notes }}</p></div>
                      </div>
                    </div>
                    <div class="modal-footer">
                      <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                      <button type="button" class="btn btn-primary" @click="printRecord"><i class="mdi mdi-printer me-1"></i> Print</button>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          <footer class="footer">
            <div class="container-fluid">
              <div class="row">
                <div class="col-sm-6"></div>
                <div class="col-sm-6"><div class="text-sm-end d-sm-block">County Hospital Management System</div></div>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import { execute, hospital } from "@/api";

export default {
  name: "PatientHistory",
  components: { NavigationBar },
  props: { id: { type: String, required: true } },

  data() {
    return {
      loading: false,
      patient: null,
      history: [],
      facilities: [],
      viewMode: 'timeline',
      currentPage: 1,
      perPage: 20,
      totalCount: 0,
      selectedItem: null,
      detailModalInstance: null,
      filters: { type: '', dateFrom: '', dateTo: '', facility: '' },
      stats: { totalVisits: 0, admissions: 0, labTests: 0, prescriptions: 0, procedures: 0, referrals: 0 }
    };
  },

  computed: {
    totalPages() { return Math.ceil(this.totalCount / this.perPage); },
    visiblePages() { const pages = []; const start = Math.max(1, this.currentPage - 2); const end = Math.min(this.totalPages, this.currentPage + 2); for (let i = start; i <= end; i++) pages.push(i); return pages; }
  },

  mounted() {
    this.detailModalInstance = new window.bootstrap.Modal(this.$refs.detailModal);
    this.loadPatient();
    this.loadHistory();
    this.loadFacilities();
  },

  methods: {
    goTo(route) { this.$router.push(route); },

    loadPatient() {
      const data = new FormData();
      data.append("function", "getPatient");
      data.append("patientID", this.id);
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      execute(data, hospital).then((res) => { if (res.data.success) this.patient = res.data.data.patient; }).catch(e => console.error('Error:', e));
    },

    loadHistory() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getPatientHistory");
      data.append("patientID", this.id);
      data.append("page", this.currentPage);
      data.append("limit", this.perPage);
      Object.keys(this.filters).forEach(key => data.append(key, this.filters[key]));
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

      execute(data, hospital).then((res) => {
        this.loading = false;
        if (res.data.success) {
          this.history = this.transformHistory(res.data.data);
          this.totalCount = res.data.data.totalCount || this.history.length;
          this.stats = { totalVisits: res.data.data.totalVisits || 0, admissions: res.data.data.totalAdmissions || 0, labTests: res.data.data.totalLabTests || 0, prescriptions: res.data.data.totalPrescriptions || 0, procedures: res.data.data.totalProcedures || 0, referrals: res.data.data.totalReferrals || 0 };
        }
      }).catch((e) => { this.loading = false; console.error('Error:', e); });
    },

    transformHistory(response) {
      const items = [];
      if (response.visits) response.visits.forEach(v => items.push({ id: 'V-' + v.visitID, type: 'Visit', date: v.visitDate || v.dateCreated, title: v.visitType + ' - ' + (v.departmentName || 'OPD'), description: v.chiefComplaint || 'General consultation', facilityName: v.facilityName, departmentName: v.departmentName, doctorName: v.doctorName, chiefComplaint: v.chiefComplaint, diagnosis: v.finalDiagnosis || v.provisionalDiagnosis, raw: v }));
      if (response.labResults) response.labResults.forEach(l => items.push({ id: 'L-' + l.resultID, type: 'Lab Result', date: l.resultDate || l.dateCreated, title: l.testName, description: `Result: ${l.result} ${l.unit || ''}`, facilityName: l.facilityName, testName: l.testName, result: l.result, unit: l.unit, normalRange: l.normalRange, abnormal: l.flag === 'H' || l.flag === 'L', raw: l }));
      if (response.prescriptions) response.prescriptions.forEach(p => items.push({ id: 'P-' + p.prescriptionID, type: 'Prescription', date: p.prescriptionDate || p.dateCreated, title: `${p.totalItems} medication(s)`, description: p.diagnosis, facilityName: p.facilityName, prescriberName: p.prescriberName, drugs: p.items || [], raw: p }));
      if (response.admissions) response.admissions.forEach(a => items.push({ id: 'A-' + a.admissionID, type: 'Admission', date: a.admissionDate || a.dateCreated, title: a.wardName + ' - ' + a.bedNumber, description: a.admittingDiagnosis, facilityName: a.facilityName, wardName: a.wardName, bedNumber: a.bedNumber, admittingDiagnosis: a.admittingDiagnosis, dischargeDate: a.dischargeDate, doctorName: a.admittingDoctorName, raw: a }));
      if (response.procedures) response.procedures.forEach(pr => items.push({ id: 'PR-' + pr.procedureID, type: 'Procedure', date: pr.startTime || pr.scheduledDate || pr.dateCreated, title: pr.procedureName, description: pr.indication, facilityName: pr.facilityName, procedureName: pr.procedureName, surgeonName: pr.surgeonName, findings: pr.findings, raw: pr }));
      if (response.referrals) response.referrals.forEach(r => items.push({ id: 'R-' + r.referralID, type: 'Referral', date: r.referralDate || r.dateCreated, title: `Referral to ${r.toFacilityName}`, description: r.referralReason, facilityName: r.fromFacilityName, fromFacilityName: r.fromFacilityName, toFacilityName: r.toFacilityName, referralReason: r.referralReason, doctorName: r.referringDoctorName, raw: r }));
      items.sort((a, b) => new Date(b.date) - new Date(a.date));
      return items;
    },

    loadFacilities() {
      const data = new FormData();
      data.append("function", "getFacilities");
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      execute(data, hospital).then((res) => { if (res.data.success) this.facilities = res.data.data.facilities || []; }).catch(e => console.error('Error:', e));
    },

    goToPage(page) { if (page >= 1 && page <= this.totalPages) { this.currentPage = page; this.loadHistory(); } },
    viewDetails(item) { this.selectedItem = item; this.detailModalInstance.show(); },
    printRecord() { window.print(); },
    exportHistory() { alert('Export feature coming soon'); },

    getTypeIcon(type) { const icons = { 'Visit': 'mdi mdi-calendar-check', 'Lab Result': 'mdi mdi-flask', 'Prescription': 'mdi mdi-pill', 'Admission': 'mdi mdi-hospital-building', 'Procedure': 'mdi mdi-medical-bag', 'Referral': 'mdi mdi-swap-horizontal' }; return icons[type] || 'mdi mdi-file-document'; },
    getTypeBadgeClass(type) { const classes = { 'Visit': 'bg-primary', 'Lab Result': 'bg-info', 'Prescription': 'bg-success', 'Admission': 'bg-warning text-dark', 'Procedure': 'bg-danger', 'Referral': 'bg-secondary' }; return classes[type] || 'bg-secondary'; },
    getMarkerClass(type) { const classes = { 'Visit': 'marker-primary', 'Lab Result': 'marker-info', 'Prescription': 'marker-success', 'Admission': 'marker-warning', 'Procedure': 'marker-danger', 'Referral': 'marker-secondary' }; return classes[type] || 'marker-secondary'; },

    formatDate(date) { if (!date) return '-'; return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); },
    formatDateTime(date) { if (!date) return '-'; return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); },
    truncate(text, length) { if (!text) return ''; return text.length > length ? text.substring(0, length) + '...' : text; },
    getInitials(name) { if (!name) return '?'; return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase(); }
  }
}
</script>

<style scoped>
.avatar-sm { width: 2.5rem; height: 2.5rem; }
.avatar-lg { width: 5rem; height: 5rem; }
.avatar-title { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; color: #fff; font-weight: 600; }
.bg-pink { background-color: #e83e8c !important; }
.btn-soft-primary { background-color: rgba(85, 110, 230, 0.1); color: #556ee6; border: none; }
.btn-soft-primary:hover { background-color: #556ee6; color: #fff; }

/* Timeline */
.timeline { position: relative; padding: 20px 0; }
.timeline::before { content: ''; position: absolute; left: 50%; transform: translateX(-50%); width: 2px; height: 100%; background: #e9ecef; }
.timeline-item { position: relative; margin-bottom: 30px; width: 50%; padding-right: 40px; }
.timeline-item-right { margin-left: 50%; padding-right: 0; padding-left: 40px; }
.timeline-marker { position: absolute; right: -15px; top: 15px; width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 12px; z-index: 1; }
.timeline-item-right .timeline-marker { left: -15px; right: auto; }
.marker-primary { background: #556ee6; }
.marker-info { background: #50a5f1; }
.marker-success { background: #34c38f; }
.marker-warning { background: #f1b44c; }
.marker-danger { background: #f46a6a; }
.marker-secondary { background: #74788d; }
.timeline-content .card { box-shadow: 0 2px 8px rgba(0,0,0,0.1); }

@media (max-width: 768px) {
  .timeline::before { left: 15px; }
  .timeline-item, .timeline-item-right { width: 100%; padding-left: 50px; padding-right: 0; margin-left: 0; }
  .timeline-marker, .timeline-item-right .timeline-marker { left: 0; right: auto; }
}
</style>
